import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  getUniboxAccounts,
  replyEstimateCommunication,
} from "../../api/Estimates/estimateRfqApi";

const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_MB = 15;

function ensureReplySubject(subject) {
  const s = String(subject || "").trim() || "Sin asunto";
  return /^re\s*:/i.test(s) ? s : `RE: ${s}`;
}

/**
 * source: {
 *   kind: 'comm' | 'rfq',
 *   item,
 * }
 */
const EstimateReplyDialog = ({ open, onClose, presupuesto, source, onSent }) => {
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [fromEmail, setFromEmail] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !source?.item) return;
    setError("");
    setSending(false);
    setCcEmail("");
    setBody("");
    setFiles([]);

    const item = source.item;
    const isIn = String(item.Direction || "").toLowerCase() === "in";
    const defaultTo =
      source.kind === "rfq"
        ? item.ToEmail || ""
        : isIn
          ? item.FromEmail || ""
          : item.ToEmail || "";
    setToEmail(String(defaultTo || "").trim());
    setSubject(ensureReplySubject(item.Subject));

    let cancelled = false;
    (async () => {
      setLoadingAccounts(true);
      try {
        const list = await getUniboxAccounts();
        if (cancelled) return;
        setAccounts(list);
        const preferred =
          list.find(
            (a) =>
              String(a?.email || "").toLowerCase() ===
              String(item.FromEmail || "").toLowerCase(),
          )?.email ||
          list.find((a) => a?.email)?.email ||
          "";
        setFromEmail(preferred);
      } catch (err) {
        if (!cancelled) {
          setAccounts([]);
          setFromEmail("");
          setError(
            err?.response?.data?.error ||
              "No se pudieron cargar las cuentas TONBOX",
          );
        }
      } finally {
        if (!cancelled) setLoadingAccounts(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, source]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setError("");
    const next = [...files];
    for (const file of incoming) {
      if (next.length >= MAX_ATTACHMENTS) {
        setError(`Máximo ${MAX_ATTACHMENTS} adjuntos.`);
        break;
      }
      if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
        setError(`"${file.name}" supera ${MAX_ATTACHMENT_MB} MB.`);
        continue;
      }
      const dup = next.some(
        (f) => f.name === file.name && f.size === file.size,
      );
      if (!dup) next.push(file);
    }
    setFiles(next);
  };

  const handleSend = async () => {
    setError("");
    if (!presupuesto?.IdPresupuesto) {
      setError("Presupuesto no válido.");
      return;
    }
    if (!fromEmail) {
      setError("Selecciona la cuenta de envío.");
      return;
    }
    if (!toEmail) {
      setError("Indica el destinatario.");
      return;
    }
    if (!String(body || "").trim()) {
      setError("Escribe el cuerpo de la respuesta.");
      return;
    }

    const payload =
      source?.kind === "rfq"
        ? { replyToRfqId: source.item.IdEstimateRfq }
        : { replyToCommId: source.item.IdEstimateCommLink };

    setSending(true);
    try {
      const result = await replyEstimateCommunication(
        presupuesto.IdPresupuesto,
        {
          ...payload,
          fromEmail,
          toEmail,
          ccEmail: ccEmail || undefined,
          subject,
          body,
        },
        files,
      );
      onSent?.(result);
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "No se pudo enviar la respuesta",
      );
    } finally {
      setSending(false);
    }
  };

  const canReply =
    source?.kind === "rfq"
      ? Boolean(source?.item?.GmailMessageId || source?.item?.MessageId)
      : Boolean(source?.item?.GmailMessageId || source?.item?.MessageId);

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Responder</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {!canReply ? (
            <Alert severity="warning">
              Este mensaje no tiene ID en TONBOX; no se puede responder en
              hilo desde aquí.
            </Alert>
          ) : null}

          {loadingAccounts ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel id="reply-from-label">Desde</InputLabel>
              <Select
                labelId="reply-from-label"
                label="Desde"
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
              >
                {accounts.map((a) => (
                  <MenuItem key={a.id || a.email} value={a.email}>
                    {a.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            label="Para"
            size="small"
            fullWidth
            value={toEmail}
            onChange={(e) => setToEmail(e.target.value)}
          />
          <TextField
            label="CC (opcional)"
            size="small"
            fullWidth
            value={ccEmail}
            onChange={(e) => setCcEmail(e.target.value)}
          />
          <TextField
            label="Asunto"
            size="small"
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <TextField
            label="Mensaje"
            size="small"
            fullWidth
            multiline
            minRows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Escribe tu respuesta…"
            helperText="Se incluirá debajo el historial del mensaje al que respondes."
          />

          <Box>
            <Button
              component="label"
              size="small"
              variant="outlined"
              startIcon={<AttachFileIcon />}
              disabled={sending || files.length >= MAX_ATTACHMENTS}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Adjuntar
              <input
                hidden
                type="file"
                multiple
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </Button>
            {files.length ? (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {files.map((file, idx) => (
                  <Stack
                    key={`${file.name}-${file.size}-${idx}`}
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                  >
                    <Chip
                      size="small"
                      label={`${file.name} (${Math.max(1, Math.round(file.size / 1024))} KB)`}
                    />
                    <IconButton
                      size="small"
                      onClick={() =>
                        setFiles((prev) => prev.filter((_, i) => i !== idx))
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Opcional · máx. {MAX_ATTACHMENT_MB} MB c/u
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || !canReply || loadingAccounts}
        >
          {sending ? "Enviando…" : "Enviar respuesta"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EstimateReplyDialog;
